import React from "react";
import {
  Box, Button, Flex, HStack, NumberInput, NumberInputField,
  Select, Table, Tbody, Td, Text, Th, Thead, Tr,
  Checkbox, Link, Skeleton, SkeletonText,
  IconButton
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { FaFilePdf } from "react-icons/fa";
import Num from '../common/Num';

const API_BASE_URL = "https://api.myseraph.eu";

/* ——————————————————— skeleton ——————————————————— */
function SkeletonRow({ showSlaColumn }) {
  return (
    <Tr>
      <Td><Skeleton h="18px" w="24px" /></Td>
      <Td><Skeleton h="18px" w="40px" /></Td>
      <Td><Skeleton h="18px" w="80px" /></Td>
      <Td><Skeleton h="18px" w="40px" /></Td>
      <Td><SkeletonText noOfLines={1} w="160px" /></Td>
      <Td isNumeric><Skeleton h="18px" w="60px" ml="auto" /></Td>
      <Td isNumeric><Skeleton h="18px" w="60px" ml="auto" /></Td>
      <Td><Skeleton h="28px" w="70px" /></Td>
      <Td><Skeleton h="18px" w="40px" /></Td>
      {showSlaColumn && <Td><Skeleton h="28px" w="110px" /></Td>}
      <Td isNumeric><Skeleton h="18px" w="70px" ml="auto" /></Td>
      <Td isNumeric><Skeleton h="18px" w="80px" ml="auto" /></Td>
    </Tr>
  );
}

/* ——————————————————— bottone paginazione ——————————————————— */
const PageBtn = ({ label, disabled, onClick }) => (
  <Button size="sm" onClick={onClick} isDisabled={disabled}>
    {label}
  </Button>
);

/* ——————————————————— tabella principale ——————————————————— */
export default function ProductsTable({
  products = [],
  slas = [],
  showSlaColumn = false,
  currentPage = 1,
  totalProducts = 0,
  itemsPerPage = 15,
  onPageChange = () => { },
  onToggleSelect = () => { },
  onQuantityChange = () => { },
  onSlaChange = () => { },
  productsLoading = false,
  periodicityDiscount = 0,
}) {
  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));

  const [visibleDesc, setVisibleDesc] = React.useState(null);

  return (
    <Box
      overflowX="auto"
      maxW="75vw"
      w="100%"
      border="1px solid"
      borderColor="gray.100"
      borderRadius="md"
      boxShadow="sm"
      bg="whiteAlpha.900"
      position="relative"
      pb={12}
      mb={8}
    >
      <Table
        size="sm"
        variant="simple"
        sx={{
          "tbody tr": { transition: "background-color 0.2s ease" },
          "tbody tr:hover": { backgroundColor: "gray.50" }
        }}
      >
        <Thead position="sticky" top="0" bg="whiteAlpha.900" backdropFilter="blur(4px)" zIndex="1">
          <Tr>
            <Th>✓</Th><Th>ID</Th><Th>Codice</Th><Th>Info</Th><Th>Descrizione</Th>
            <Th isNumeric>Listino/mese</Th><Th isNumeric>Prezzo riservato</Th>
            <Th>Q.ta</Th><Th>% sconto</Th>
            {showSlaColumn && <Th>SLA</Th>}
            <Th isNumeric>Totale listino</Th>
            <Th isNumeric>Totale riservato</Th>
          </Tr>
        </Thead>

        <Tbody>
          {productsLoading
            ? [...Array(itemsPerPage)].map((_, i) => (
                <SkeletonRow key={i} showSlaColumn={showSlaColumn} />
              ))
            : products.map((p) => (
                <Tr key={`${p.product_code}-${p.quantity}`}>
                  <Td>
                    <Checkbox
                      borderColor="darkgray"
                      isChecked={p.selected}
                      onChange={() => onToggleSelect(p.product_code)}
                    />
                  </Td>
                  <Td>{p.id}</Td>
                  <Td>{p.product_code}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        icon={<InfoOutlineIcon />}
                        size="sm"
                        variant="ghost"
                        aria-label="Mostra descrizione"
                        onClick={() =>
                          setVisibleDesc({
                            productCode: p.product_code,
                            description: p.extended_description,
                          })
                        }
                      />
                      {p.product_datasheet && (
                        <Link
                          href={`${API_BASE_URL}/storage/${p.product_datasheet}`}
                          isExternal
                        >
                          <FaFilePdf />
                        </Link>
                      )}
                    </HStack>
                  </Td>
                  <Td>{p.description}</Td>
                  <Td isNumeric><Num value={p.price_per_month} fmt="€" /></Td>
                  <Td isNumeric><Num value={p.reserved_per_month} fmt="€" /></Td>
                  <Td>
                    <NumberInput
                      size="sm"
                      w="70px"
                      value={p.quantity}
                      min={1}
                      keepWithinRange={false}
                      clampValueOnBlur={false}
                      onChange={(vs, vn) => {
                        if (vs === "") {
                          onQuantityChange(p.product_code, "", p.sla_id);
                        } else if (!Number.isNaN(vn) && vn > 0) {
                          onQuantityChange(p.product_code, vn, p.sla_id);
                        }
                      }}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </Td>
                  <Td isNumeric><Num value={periodicityDiscount} fmt="%" /></Td>
                  {showSlaColumn && (
                    <Td>
                      <Select
                        size="xs"
                        w="110px"
                        value={p.sla_id ?? ""}
                        onChange={(e) =>
                          onSlaChange(p.product_code, e.target.value, p.quantity)
                        }
                      >
                        <option value="">–</option>
                        {slas.map((s) => (
                          <option key={s.id} value={s.id}>{s.titolo}</option>
                        ))}
                      </Select>
                    </Td>
                  )}
                  <Td isNumeric><Num value={p.totale} fmt="€" /></Td>
                  <Td isNumeric><Num value={p.totale_scontato} fmt="€" /></Td>
                </Tr>
              ))}
        </Tbody>
      </Table>

      {/* ——— Overlay laterale descrizione estesa ——— */}
      {visibleDesc && (
        <Box
          position="absolute"
          top="60px"
          right="8%"
          width="720px"
          height="300px"
          bg="white"
          boxShadow="2xl"
          borderRadius="md"
          zIndex="100"
          p={4}
          overflowY="auto"
          border="1px solid #ccc"
        >
          <Flex justify="space-between" align="center" mb={2} borderBottom="1px solid #ccc" pb={2}>
            <Text fontWeight="bold">Descrizione estesa</Text>
            <IconButton
              size="sm"
              icon={<span style={{ fontWeight: "bold" }}>✕</span>}
              onClick={() => setVisibleDesc(null)}
              aria-label="Chiudi"
              variant="ghost"
            />
          </Flex>
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {visibleDesc.description || "Nessuna descrizione disponibile."}
          </Text>
        </Box>
      )}

      {/* ——— Paginazione ——— */}
      <Flex position="absolute" right="1rem" bg="white" borderRadius="md" boxShadow="sm">
        <Text mr={4} alignSelf="center" mt={2}>
          {totalProducts
            ? `${(currentPage - 1) * itemsPerPage + 1}–${Math.min(
                currentPage * itemsPerPage, totalProducts)} di ${totalProducts}`
            : "Nessun risultato"}
        </Text>
        <HStack spacing={2} alignItems="center">
          <PageBtn label="«" disabled={currentPage === 1} onClick={() => onPageChange(1)} />
          <PageBtn label="←" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} />
          <Text fontWeight="bold" mt={2}>{currentPage}/{totalPages}</Text>
          <PageBtn label="→" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} />
          <PageBtn label="»" disabled={currentPage === totalPages} onClick={() => onPageChange(totalPages)} />
        </HStack>
      </Flex>
    </Box>
  );
}
